import React from "react";
import moment from "moment";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import Sociedade from "../../common/query/Sociedade";
import { formatDocument, formatToReal } from "../../../utils/utils";

const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};

const LocalizacaoPJQuery = ({ data }) => {
  const cadastro = data?.dadosCadastrais || {};
  const foundationMoment = cadastro.dataFundacao
    ? moment(cadastro.dataFundacao, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"], true)
    : null;
  const foundationLabel =
    foundationMoment && foundationMoment.isValid()
      ? foundationMoment.format("DD/MM/YYYY")
      : null;
  const foundationDuration = foundationMoment
    ? buildDurationLabel(foundationMoment, "de atividade")
    : null;

  const summaryCards = [
    {
      label: "CNAE principal",
      value: cadastro.ramoAtividade || "Não informado",
      hint: "Atividade",
    },
    {
      label: "Natureza jurídica",
      value: cadastro.naturezaJuridica || cadastro.natureza || "Não informado",
      hint: "Perfil",
    },
    {
      label: "Capital social",
      value:
        formatToReal(cadastro.capitalInicial || cadastro.capitalSocial) ||
        "Não informado",
      hint: "Recursos",
    },
    {
      label: "Inscrição Estadual",
      value: cadastro.inscricaoEstadual || "Não informado",
      hint: "Registro",
    },
  ];

  return (
    <div className="space-y-10 mt-8">
      <div>
        <TitleStripe>📄 Dados Cadastrais</TitleStripe>
        <div className="mt-4 rounded-[24px] border border-gray-200 bg-white/80 p-4 shadow-sm text-xs">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                Razão Social
              </p>
              <p className="text-xl font-semibold text-gray-900">
                {cadastro.razaoSocial || "Indisponível"}
              </p>
              <p className="text-[11px] text-gray-500">
                {cadastro.nomeFantasia || "Sem nome fantasia"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                CNPJ
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {formatDocument(cadastro.cnpj) || "--"}
              </p>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              Situação: {cadastro.situacao || "Desconhecida"}
            </span>
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              Fundação: {foundationLabel || "—"}
            </span>
            {foundationDuration && (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                {foundationDuration}
              </span>
            )}
          </div>

          <div className="mt-3 grid gap-2 grid-cols-2 md:grid-cols-4">
            {summaryCards.map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-gray-100 bg-slate-50 px-3 py-2"
              >
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                  {item.label}
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {item.value}
                </p>
                <p className="text-[10px] text-gray-500">{item.hint}</p>
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-xl border border-gray-100 bg-white px-3 py-2 text-[10px]">
            <p className="font-semibold text-gray-700">Local</p>
            <p className="text-sm font-medium text-gray-900">
              {[cadastro.endereco, cadastro.numero]
                .filter(Boolean)
                .join(", ") || "—"}
            </p>
            <p className="text-[10px] text-gray-500">
              {[cadastro.bairro, cadastro.cidade, cadastro.uf]
                .filter(Boolean)
                .join(" · ") || "—"}
            </p>
          </div>
        </div>
      </div>

      <div>
        <TitleStripe>👥 Sócios</TitleStripe>
        {data?.socios?.length > 0 ? (
          <Sociedade socios={data?.socios} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe>📍 Endereços</TitleStripe>
        {data?.enderecos?.length > 0 ? (
          <Enderecos enderecos={data?.enderecos} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe>📧 E-mails</TitleStripe>
        {data?.emails?.length > 0 ? (
          <Emails emails={data?.emails} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe>📞 Telefones</TitleStripe>
        {data?.telefones?.length > 0 ? (
          <Telefones telefones={data?.telefones} />
        ) : (
          <NoOcurrence />
        )}
      </div>
    </div>
  );
};

export default LocalizacaoPJQuery;
